import React, { useCallback, useRef, useState } from 'react';

import Lottie, { type LottieRefCurrentProps } from 'lottie-react';

import { ThemeProvider } from '@atlaskit/app-provider';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, cx } from '@atlaskit/css';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { Box, type BoxProps } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	container: {
		position: 'relative',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	control: {
		position: 'absolute',
		insetBlockEnd: token('space.100'),
		insetInlineEnd: token('space.100'),
	},
	large: {
		width: '100%',
		'@media (min-width: 30rem)': {
			// On larger viewports, they are laid out in two columns.
			width: '756px',
			height: '280px',
		},
	},
	wide: {
		width: '100%',
		'@media (min-width: 30rem)': {
			// On larger viewports, they are laid out in two columns.
			width: '756px',
			height: '160px',
		},
	},
	small: {
		width: '100%',
		'@media (min-width: 30rem)': {
			// On larger viewports, they are laid out in two columns.
			width: '370px',
			height: '160px',
		},
	},
	border: {
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
	},
	spacing: {
		marginBlockStart: token('space.400'),
		marginBlockEnd: token('space.400'),
	},
});

export interface IllustrationProps {
	label?: string;
	size?: 'small' | 'wide' | 'large';
	lottieFile: object;
	xcss?: BoxProps<'div'>['xcss'];
	hasBorder?: boolean;
	hasSpacing?: boolean;
}

/**
 * Lottie animation component
 */
const Illustration = ({
	label,
	lottieFile,
	xcss,
	size,
	hasBorder = false,
	hasSpacing = true,
}: IllustrationProps) => {
	const [playing, setPlaying] = useState(false);
	const lottieRef = useRef<LottieRefCurrentProps>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	const patchSvgFilterBounds = useCallback(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		container.querySelectorAll('filter').forEach((filter) => {
			filter.setAttribute('x', '-10%');
			filter.setAttribute('y', '-10%');
			filter.setAttribute('width', '120%');
			filter.setAttribute('height', '120%');
		});
	}, []);

	const togglePlay = () => {
		setPlaying((val) => {
			if (!val) {
				lottieRef.current?.play();
			} else {
				lottieRef.current?.pause();
			}
			return !val;
		});
	};

	return (
		<ThemeProvider defaultColorMode="light">
			<Box
				ref={containerRef}
				xcss={cx(
					styles.container,
					xcss,
					size && styles[size],
					hasBorder && styles.border,
					hasSpacing && styles.spacing,
				)}
			>
				<Lottie
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
						height: '100%',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						width: '100%',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						borderRadius: token('radius.xlarge'),
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						overflow: 'hidden',
					}}
					lottieRef={lottieRef}
					animationData={lottieFile}
					autoplay={false}
					loop={true}
					onDOMLoaded={patchSvgFilterBounds}
					onLoopComplete={() => {
						setPlaying(false);
						lottieRef.current?.goToAndStop(0);
					}}
					role="img"
					aria-label={label}
				/>
				<Box xcss={styles.control}>
					<IconButton
						spacing="compact"
						icon={
							playing
								? () => <VideoPauseOverlayIcon size="small" label="" />
								: () => <VideoPlayOverlayIcon size="small" label="" />
						}
						label={playing ? 'Pause' : 'Play'}
						onClick={togglePlay}
					/>
				</Box>
			</Box>
		</ThemeProvider>
	);
};

export default Illustration;
